import React, { useState } from 'react';

import { Typography, Image, Divider, Alert, Space } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

import {
  FormAddButton,
  FormDeleteButton,
  FormEditButton,
  StyledModal,
  WithLoading,
  EditButtonsSection,
} from 'common/components';
import Marquee from 'react-fast-marquee';

import {
  useListenerMessageSaveCard,
  useVisibleModal,
  useMemberPaymentActions,
} from './hooks';

import { useCreditCardEula } from 'hooks/useCreditCardEula';
import useDocumentAttachEvent from 'hooks/documentAttachEventHook';
import { useCheckCreditCardPermission } from 'hooks/useCheckCreditCardPermission';

import { EVENT } from 'static/Constants';

import CreditCardImage from 'assets/credit-card.png';
import AchPaymentImage from 'assets/ACH-payment.png';
import { ReactComponent as JpmLogo } from 'assets/credit-card/logo-jpmorgan.svg';
// import authorizeNet from 'assets/credit-card/anetlogo.png';
import { useGetSavedMemberCard } from './hooks';
import { useCheckInvoicedPayment } from 'hooks/home/useCreditCardAlert';
import { useGetMemberId, useUserInfo } from 'hooks';
import OutstandingBillingModal from './outstanding-billing/OutstandingBillingModal';
import { useQueryOutstandingBilling } from 'pages/check-credit-card-info/queries';

const MemberCardInformation = () => {
  const [visible, { onModal, offModal }] = useVisibleModal();
  const [isEdit, setEdit] = useState(false);

  const { memberId } = useGetMemberId();
  const { userInfo } = useUserInfo();
  const [showOutstandingBillingModal, setShowOutstandingBillingModal] =
    useState(false);

  const [modeAction, setModeAction] = useState(null);

  const { savedMemberCard, isFetchingSavedCard, fetchSavedMemberCard } =
    useGetSavedMemberCard({
      modeAction,
    });

  const { fetchOutstandingBillingAsync } = useQueryOutstandingBilling({
    enabled: false,
    memberId,
  });

  const onSuccessCallback = async () => {
    const data = await fetchOutstandingBillingAsync();

    if (data?.hasOutstandingFees) {
      setShowOutstandingBillingModal(true);
    }
  };

  useListenerMessageSaveCard({
    offModal,
    fetchSavedMemberCard,
    modeAction,
    onSuccessCallback,
  });

  const { iframeUrl, loadingIframe, handleSaveCard, handleRemoveSavedCard } =
    useMemberPaymentActions({
      memberPaymentProfileId: savedMemberCard?.memberPaymentProfileId,
      fetchSavedMemberCard,
      offModal,
    });

  const { showCardEulaModal } = useCreditCardEula({
    context: 'billing',
  });

  const { canActionCard, hasDeleteCardPermission } =
    useCheckCreditCardPermission();

  const { isInvoicedTerm } = useCheckInvoicedPayment();

  const hasSavedMemberCard = Boolean(savedMemberCard?.memberPaymentProfileId);

  const paymentImage =
    savedMemberCard?.paymentType === 'E-Check'
      ? AchPaymentImage
      : CreditCardImage;

  const onEditCard = () => {
    showCardEulaModal({ when: 'edit-card' });
  };

  const onAddCard = () => {
    showCardEulaModal({ when: 'add-card' });
  };

  const startEditCard = () => {
    setModeAction('edit');
    onModal();
    handleSaveCard({ modeAction: 'edit' });
  };

  const startAddCard = () => {
    setModeAction('add');
    onModal();
    handleSaveCard({ modeAction: 'add' });
  };

  const onAcceptCreditCardEula = (e) => {
    const when = e?.detail?.when;

    const actionCollection = {
      'add-card': startAddCard,
      'edit-card': startEditCard,
    };

    actionCollection[when]?.();
  };

  useDocumentAttachEvent({
    name: EVENT.ACCEPT_CARD_EULA,
    handler: onAcceptCreditCardEula,
    dependencies: [savedMemberCard?.memberPaymentProfileId],
  });

  const canDeleteCard = hasDeleteCardPermission && userInfo?.member?.id === 1;

  return (
    <>
      <div>
        <WithLoading loading={isFetchingSavedCard}>
          <div className='member-tab-billing__section-edit-title'>
            <Typography.Text strong style={{ fontSize: 20 }}>
              Payment Options
            </Typography.Text>
            {(canActionCard || canDeleteCard) && (
              <EditButtonsSection
                isEdit={isEdit}
                onEdit={() => setEdit(true)}
                onCancel={() => {
                  setEdit(false);
                }}
                buttons={['cancel', canActionCard ? 'edit' : '']}
              />
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Image
              src={paymentImage}
              width={70}
              preview={false}
            />
            <>
              {hasSavedMemberCard ? (
                <>
                  <Typography.Text
                    strong
                    style={{ color: '#6c757d', fontSize: 15 }}
                  >
                    {savedMemberCard?.nameAlias ?? 'xxxx card'}
                  </Typography.Text>

                  {isEdit ? (
                    <div style={{ gap: 3, display: 'flex' }}>
                      {canActionCard ? (
                        <FormEditButton
                          icon={<EditOutlined />}
                          onClick={onEditCard}
                          size='small'
                          text={null}
                        />
                      ) : null}
                      {canDeleteCard ? (
                        <FormDeleteButton
                          icon={<DeleteOutlined />}
                          size='small'
                          text={null}
                          onClick={handleRemoveSavedCard}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : (
                <EmptyCardRender
                  isEdit={isEdit}
                  onAddCard={onAddCard}
                  isInvoicedTerm={isInvoicedTerm}
                />
              )}
            </>
          </div>
        </WithLoading>
      </div>

      {visible && (
        <CreditCardIframeModal
          title={
            <>
              <Typography.Paragraph style={{ marginBottom: 4 }}>
                <Typography.Text style={{ fontWeight: '700' }}>
                  {modeAction === 'edit'
                    ? 'Edit Payment Method'
                    : 'Add Payment Method'}
                </Typography.Text>
              </Typography.Paragraph>
              <Typography.Paragraph style={{ fontWeight: '400', margin: 0 }}>
                This card or eCheck will be used for annual membership dues,
                product captures, and authorized ancillary charges.{' '}
                <Typography.Text style={{ fontWeight: '700' }}>
                  eCheck is our preferred payment method; please scroll down to
                  select this option
                </Typography.Text>
              </Typography.Paragraph>
            </>
          }
          visible={visible}
          onCancel={() => {
            offModal();
            setModeAction(null);
          }}
        >
          <WithLoading loading={loadingIframe}>
            <CreditCardIframe iframeUrl={iframeUrl} />
          </WithLoading>
        </CreditCardIframeModal>
      )}

      {showOutstandingBillingModal && (
        <OutstandingBillingModal
          visible={showOutstandingBillingModal}
          setVisible={setShowOutstandingBillingModal}
        />
      )}
    </>
  );
};

export const CreditCardIframeModal = (props) => {
  const { children, ...rest } = props;

  return (
    <StyledModal
      wrapClassName='member-payment__modal'
      bodyStyle={{
        height: '70vh',
      }}
      width={1032}
      footer={<CreditCardFooter />}
      maskClosable={false}
      {...rest}
    >
      <CreditCardHeader />
      <div className='scroller--y' style={{ height: 'calc(100% - 32px)' }}>
        {children}
      </div>
    </StyledModal>
  );
};

export const CreditCardIframe = ({ iframeUrl, ...rest }) => {
  return (
    <iframe
      className='scroller--y'
      loading='lazy'
      src={iframeUrl}
      title='Save Payment Information'
      frameBorder='0'
      style={{ width: '100%', height: '100%' }}
      {...rest}
    ></iframe>
  );
};

const EmptyCardRender = ({ isEdit, onAddCard, isInvoicedTerm }) => {
  if (isEdit) {
    return (
      <FormAddButton text='Add Payment Information' ghost onClick={onAddCard} />
    );
  }

  return null;
};

export const AlertEmptyCard = ({ isInvoicedTerm = false }) => {
  let message = isInvoicedTerm
    ? 'With an invoiced term, no eCheck or payment information is required.'
    : 'To continue accessing all our service features, please add a payment to your account.';

  return (
    <Alert
      banner
      message={
        <Marquee pauseOnHover gradient={false} speed={30}>
          <div style={{ marginRight: 100 }}>{message}</div>
        </Marquee>
      }
    />
  );
};

const CreditCardHeader = () => {
  return (
    <div style={{ textAlign: 'right', paddingRight: 12 }}>
      <JpmLogo width={160} />
    </div>
  );
};

const CreditCardFooter = () => {
  return (
    <>
      <div className='member-payment__modal--footer'>
        <Space align='start' size={8}>
          <Typography.Text strong style={{ fontSize: 16 }}>
            RIVIR has partnered with
          </Typography.Text>
          <JpmLogo width={136} />
          <Typography.Text strong style={{ fontSize: 16 }}>
            to store your payment methods securely
          </Typography.Text>
        </Space>
      </div>
      <div>
        <Divider
          style={{
            width: '50%',
            minWidth: '50%',
            margin: '4px auto 10px',
            borderTop: '2px solid #006d75',
          }}
        />
      </div>
    </>
  );
};

export default MemberCardInformation;
